import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

/*
  TSOGN — Chain 1, Episode 1: The First Walker
  ------------------------------------------------------------------
  V3.4 LOCKED — editorial team final, May 2026.
  Prose, receipts, beat copy, and quiz are NOT to be modified here.
  This file is the PLAYER, not the script.

  PLAYBACK CONTRACT (per founder's hand-off, May 2026):
  - Press play once and the chain runs end-to-end. Voice-led.
  - Image, receipts, narration synchronized per beat.
  - When the beat's audio ends, advance to the next beat automatically.
  - If a beat's audio is not yet wired (TODO_AUDIO), fall back to a
    word-count-based duration at ~145 wpm so the chain still plays
    end-to-end during the silent-pre-narration review window.
  - Controls: pause, back one beat, skip one beat, mute. That is enough.
  - Receipts are offers, not gates. They appear with the claim and leave
    when the beat ends.
  - Bridge "Next · The Shaped Stone" button uses react-router useNavigate.
    The shaped-stone chain is a 404 until that route ships; the placeholder
    page lives at /chains/the-shaped-stone (Coming Next card).

  IMAGES: all 15 beat images + teaser + bridge generated via GPT Image 1
  in one consistent cinematic style, served from
  /assets/chains/first-walker/*.jpg
*/

const COLORS = {
  ash: "#0d0c0a",
  ashSoft: "#15130f",
  bone: "#e8e2d4",
  boneDim: "#a8a08c",
  ember: "#c2703d",
  emberSoft: "#8a5836",
  line: "#2a261f",
};

const IMG = (slug) => `/assets/chains/first-walker/${slug}.jpg`;

const CHAIN = {
  id: "chain-1-ep-1-first-walker",
  number: "Chain 1 · Episode 1",
  title: "The First Walker",
  methodologyCard:
    "Everything here is sourced. The receipts appear on screen as we go. You never have to take my word for it.",
  beats: [
    {
      movement: "origin",
      image: IMG("beat-01-feet-present-day"),
      audio: "TODO_AUDIO_beat01.mp3",
      text:
        "Hey. Sit down for this one. Pull up a chair. I want to start with your feet. Right now, however you are sitting, your feet are doing something almost no other mammal does the way you do. Hold that thought. We are coming back to it, and when we do, it is going to land different.",
      receipts: [],
    },
    {
      movement: "origin",
      image: IMG("beat-02-laetoli-print"),
      audio: "TODO_AUDIO_beat02.mp3",
      text:
        "What I am about to show you is from about 3.66 million years ago, and the first time I read that date I did not believe it. I checked it three times. It is one of the most checked things in the whole record. And it should not exist, because the thing it captured almost never survives. Footprints. Not bones. Not tools. Footprints. The actual impression of somebody walking across the ground, in what is now Tanzania, at a place called Laetoli.",
      receipts: [
        {
          label: "Leakey & Hay, Nature 278, 317–323 (1979)",
          url: "https://www.nature.com/articles/278317a0",
        },
        {
          label:
            "Deino, in Harrison ed., Paleontology and Geology of Laetoli (2011) — ⁴⁰Ar/³⁹Ar, ~3.66 Ma",
          url: "https://link.springer.com/book/10.1007/978-90-481-9956-3",
        },
      ],
    },
    {
      movement: "origin",
      image: IMG("beat-03-ashfall-landscape"),
      audio: "TODO_AUDIO_beat03.mp3",
      text:
        "Here is how you get a footprint to last that long, and it is so unlikely it is almost funny. A nearby volcanic eruption lays down fine ash, powdery, like wet cement. It rains. The ash goes soft. Something walks across it. Then the sun bakes it hard, and more ash falls and seals it. Then it sits. Continents drift. Ice ages come and go. Whole species rise up and vanish. And the prints stay.",
      receipts: [],
    },
    {
      movement: "origin",
      image: IMG("beat-04-living-landscape-tracks"),
      audio: "TODO_AUDIO_beat04.mp3",
      text:
        "Now hold still in that landscape for a second, because they were not alone out there. The same ash held the tracks of the animals moving through it. Hyenas. Baboons. Giraffes. Rhinos. Hipparion, a three-toed horse relative. Hares. Birds. More than twenty kinds of animal, pressed into the same ground. And raindrops. You can see where the rain hit. So picture it without me adding anything that is not in the ground. Wet ash. Rain coming down. Animals crossing. And somewhere in that, figures walking on two legs. We are not looking at a year, or a season. We are looking at a narrow window of time, held still.",
      receipts: [
        {
          label:
            "Leakey & Harris, eds. Laetoli: A Pliocene Site in Northern Tanzania (1987)",
          url: "https://global.oup.com/",
        },
      ],
    },
    {
      movement: "origin",
      image: IMG("beat-05-excavation-1976-1978"),
      audio: "TODO_AUDIO_beat05.mp3",
      text:
        "How we found it is the kind of thing I love. In 1976, a scientist named Andrew Hill was out at Laetoli visiting Mary Leakey's team. Walking back to camp, the crew were throwing dried elephant dung at each other, the way people actually do on long, hot digs. Hill ducked one, lost his footing, and went down. And from there, face close to the ground, he saw them. Animal prints in the ash. That is how it surfaced. A man dodging flying dung, on his way down, eye to eye with the ground. The long hominin trail itself came a couple of years later, in 1978.",
      receipts: [
        {
          label: "Yale Peabody Museum — Laetoli discovery account",
          url: "https://peabody.yale.edu/",
        },
        {
          label:
            "Agnew & Demas, \u201CPreserving the Laetoli Footprints,\u201D Scientific American (1998); Getty Conservation Institute field reports",
          url: "https://www.scientificamerican.com/article/preserving-the-laetoli-footprints/",
        },
      ],
    },
    {
      movement: "origin",
      image: IMG("beat-06-site-g-trackway"),
      audio: "TODO_AUDIO_beat06.mp3",
      text:
        "That trail, the one most people mean when they say the Laetoli footprints, is called Site G. About seventy footprints. Almost twenty-seven meters. Three individuals, walking the same direction. A larger one. A smaller one. And a third, walking in the steps of one of the others. These prints were most likely made by Australopithecus afarensis, a small-brained early hominin whose fossils turn up in the same beds. Footprints alone cannot prove the species beyond doubt, but afarensis is the one that fits.",
      receipts: [
        {
          label: "Leakey & Hay, Nature (1979)",
          url: "https://www.nature.com/articles/278317a0",
        },
        {
          label: "Masao et al., eLife 5, e19568 (2016)",
          url: "https://elifesciences.org/articles/19568",
        },
        {
          label: "Smithsonian Human Origins Program",
          url: "https://humanorigins.si.edu/",
        },
      ],
    },
    {
      movement: "change",
      image: IMG("beat-07-gait-diagram"),
      audio: "TODO_AUDIO_beat07.mp3",
      text:
        "And when you read the prints, the story they tell is specific, and I am only going to tell you what the prints actually show. Nothing I am adding. The heel strikes first. The weight rolls forward through the foot. A push off at the front. That is not how an ape walks. An ape carries its weight on the outside of the foot. These prints show a heel-to-toe roll, with the leg coming through underneath the body. Economical. Upright. The team that ran the biomechanics was careful about what they claimed. They did not say these were modern people. They said the way the weight moved through these feet was human-like in key respects \u2014 heel strike, toe-off, upright walking \u2014 more like us than like an ape. Later work pushed back on how far that goes. The stride may not have been identical to ours, the limb maybe a little more flexed. So I will say it the careful way. The pattern was already recognizably human-like, even if it was not yet exactly our own.",
      receipts: [
        {
          label: "Raichlen et al., PLOS ONE 5(3): e9769 (2010)",
          url: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0009769",
        },
        {
          label: "Hatala et al., Proceedings of the Royal Society B 283, 20160235 (2016)",
          url: "https://royalsocietypublishing.org/doi/10.1098/rspb.2016.0235",
        },
      ],
    },
    {
      movement: "change",
      image: IMG("beat-08-smaller-print-trail-ending"),
      audio: "TODO_AUDIO_beat08.mp3",
      text:
        "And that third walker, the smaller one, stepping inside the larger prints. The prints show a smaller individual placing its feet inside the tracks of a larger one. Make of that what you will. That is all the ash gave us. It did not give us names. It did not give us where they were going, or why, or what happened to them before that day or after it. Figures walking the same direction, and then the trail ends under the next layer of ash. That is where the record stops. We do not get to follow them home.",
      receipts: [],
    },
    {
      movement: "change",
      image: IMG("beat-09-open-savanna"),
      audio: "TODO_AUDIO_beat09.mp3",
      text:
        "And the next part is where the clean story starts to come apart. For most of the last hundred years, the popular explanation for why anything stood up went like this. The forests shrank. The grasslands spread. Apes came down out of the trees onto the open plains, stood up to see over the grass and to cover ground, and the ones who walked survived. Standing up was the answer to a world opening into grass. That open-country idea got seeded back in 1925, by an anatomist named Raymond Dart, and over the decades it fed into the version a lot of us absorbed somewhere along the way.",
      receipts: [
        {
          label: "Dart, Nature 115, 195–199 (1925)",
          url: "https://www.nature.com/articles/115195a0",
        },
      ],
    },
    {
      movement: "change",
      image: IMG("beat-10-soil-carbon-section"),
      audio: "TODO_AUDIO_beat10.mp3",
      text:
        "And the open-ground part is not a fairy tale. It has hard evidence behind it. In 2011, a team measured the carbon locked in ancient eastern African soils going back six million years, the chemical signature of grass versus trees. At most hominin sites in that dataset, woody cover came in under forty percent. A lot of open ground. The grasslands were real. The drying was real. So keep that standing. The open-habitat part of the story is backed by real data.",
      receipts: [
        {
          label:
            "Cerling et al., Nature 476, 51–56 (2011) — Awash & Omo-Turkana basins, eastern Africa",
          url: "https://www.nature.com/articles/nature10306",
        },
      ],
    },
    {
      movement: "contradiction",
      image: IMG("beat-11-walk-before-tools"),
      audio: "TODO_AUDIO_beat11.mp3",
      text:
        "But here is the reversal, and I am going to lay it out and let it stand. I am not going to resolve it for you. That is not my job. My job is to show you what is in the record, with the names on it, so you can hold it yourself. The older, brain-first stories treated walking as a late achievement, something that came after the big brain and after tools. That picture turned out to be backwards. By the time these footprints were uncovered, fossils like Lucy had already shown scientists that upright walking came before large brains. Laetoli did not reveal that for the first time. Laetoli made it visible in the ground. The walk is right there, human-like and upright, 3.66 million years ago, in a small-brained creature. And the oldest known stone tools come hundreds of thousands of years later, around 3.3 million years ago at a site called Lomekwi, with the more familiar toolmaking later still. The walk came first. The story got told like walking was the final achievement. The ground says it was the opening move.",
      receipts: [
        {
          label:
            "Harmand et al., Nature 521, 310–315 (2015) — 3.3 Ma stone tools, Lomekwi 3, Kenya",
          url: "https://www.nature.com/articles/nature14464",
        },
        {
          label:
            "Disputed: McPherron et al., Nature 466 (2010) — possible tool-assisted butchery at Dikika ~3.39 Ma; debated, not widely accepted",
          url: "https://www.nature.com/articles/nature09248",
        },
      ],
    },
    {
      movement: "contradiction",
      image: IMG("beat-12-laetoli-mosaic"),
      audio: "TODO_AUDIO_beat12.mp3",
      text:
        "And one more thing, so the picture stays honest. That clean open-plains story is messier than it sounds, because the ground right where these prints were made was studied closely, and it was not a treeless plain. It was a mosaic. Woodland, bushland, and grass mixed together, wetter and more wooded than the dry plain there today, with forest along the watercourses. So the wide view across eastern Africa says open ground was spreading. The close view at Laetoli says this particular place was a wooded mosaic. Both are in the record. The world was messier than a clean line from forest to grass. I am not telling you which matters more. The walk was already there, inside that messier world.",
      receipts: [
        {
          label: "Andrews & Bamford, Journal of Human Evolution 54, 78–98 (2008)",
          url: "https://www.sciencedirect.com/science/article/abs/pii/S0047248407001364",
        },
      ],
    },
    {
      movement: "contradiction",
      image: IMG("beat-13-acacia-roots"),
      audio: "TODO_AUDIO_beat13.mp3",
      weirdFact: true,
      text:
        "Here is your weird fact before we land this. The ash that should have erased that morning is the only reason we have it. And then, almost in our own time, the thing that nearly destroyed it was life itself. After the prints were uncovered and reburied to protect them, acacia trees grew in over the site, and their roots worked down into the ash, cracking the very surface the ash had saved. A team had to come back, clear the roots, and seal the site again under a protective layer. One of the oldest direct records of upright hominin walking we have, and it was nearly taken out by tree roots reaching down toward the same ground we walked away from. Destruction saved it. Then life almost took it back.",
      receipts: [
        {
          label:
            "Getty Conservation Institute Laetoli Project field reports; Agnew & Demas, Scientific American",
          url: "https://www.getty.edu/conservation/",
        },
      ],
    },
    {
      movement: "survived",
      image: IMG("beat-14-your-foot-bridge"),
      audio: "TODO_AUDIO_beat14.mp3",
      physical: true,
      text:
        "Okay. Step out of the ash with me. Come back to now. Look at your own foot. Take your shoe off if you want. Look at how your big toe lines up with the others instead of sticking out sideways like a thumb. Press your heel down and feel the weight roll forward to the ball of your foot. That roll, heel to toe, and that aligned big toe, are the things the prints in the ash recorded. Whether the arch of that foot was already fully modern is still debated. But the big toe, and the roll, are right there in the ground.",
      receipts: [],
    },
    {
      movement: "survived",
      image: IMG("beat-15-what-survived"),
      audio: "TODO_AUDIO_beat15.mp3",
      text:
        "The basic plan of that foot is older than your species. Older than fire. Older than the first word anyone ever said. The walkers at Laetoli had that foot before they had almost anything else we call human. It carried down, changed but recognizable, all the way into your shoe. Before any flag. Before any border. Before any name anyone ever called you, or you ever called anyone else. The walk was already here. That is what survived.",
      receipts: [],
    },
  ],
  teaser:
    "Laetoli holds more than one trail. Since 2016, a second set of walkers at Site S. And the old Site A trail, once argued to be a bear, was re-examined in 2021 and read as a hominin, with a foot built differently from the Site G walkers. Possibly more than one kind of upright walker, the same landscape. That story is coming.",
  teaserImage: IMG("teaser-site-s-site-a"),
  teaserReceipts: [
    {
      label: "Masao et al., eLife (2016)",
      url: "https://elifesciences.org/articles/19568",
    },
    {
      label: "McNutt et al., Nature 600, 468–472 (2021)",
      url: "https://www.nature.com/articles/s41586-021-04187-7",
    },
  ],
  bridge:
    "So they walked. Hands free. About 3.66 million years before you sat down to watch this. And that is the thing I cannot stop turning over. Walking only carries you through the world. It does not change it. But those hands were free now, and as far as we have found, the oldest known stone tools come hundreds of thousands of years after this. So what finally made a hand close around a rock, and strike it, and change the world back? That is where we go next.",
  bridgeImage: IMG("bridge-hand-and-stone"),
  bridgeAudio: "TODO_AUDIO_bridge.mp3",
  nextLabel: "Next · The Shaped Stone",
  nextPath: "/chains/the-shaped-stone",
  quiz: [
    {
      q: "What did the record at Laetoli actually preserve?",
      options: [
        "Bones of the walkers",
        "Stone tools",
        "Footprints pressed into hardened volcanic ash",
        "Cave paintings",
      ],
      correct: 2,
      feedback:
        "The Laetoli record is footprints in hardened volcanic ash — not bones or tools. That is what makes it so rare.",
    },
    {
      q: "What main reversal does this chain show?",
      options: [
        "Upright, human-like walking was already present about 3.66 million years ago, before large brains and before the oldest known stone tools",
        "The footprints were faked",
        "The ash could not be dated",
        "There is no reversal",
      ],
      correct: 0,
      feedback:
        "The walk came first. Upright walking is recorded long before large brains and before the oldest known stone tools.",
    },
    {
      q: "What did the Site G prints show about how these individuals moved?",
      options: [
        "A single individual running on all fours",
        "Weight carried on the outside of the foot, like an ape",
        "A human-like heel-strike and toe-off, upright gait, with three individuals moving the same direction and a smaller one stepping inside the larger tracks",
        "The prints were too damaged to read",
      ],
      correct: 2,
      feedback:
        "Heel-strike to toe-off, upright, three walkers in the same direction, one smaller individual stepping inside the larger tracks.",
    },
  ],
};

const MOVEMENTS = [
  { key: "origin", label: "Origin" },
  { key: "change", label: "The Standard Story" },
  { key: "contradiction", label: "Standing Side by Side" },
  { key: "survived", label: "What Survived" },
];

// PHASE_TITLE plays before the beats so users see the title and the
// methodology card briefly before the chain begins. PHASE_TEASER and
// PHASE_BRIDGE play after the 15 beats. PHASE_QUIZ is the only phase that
// stops the autoplay loop and waits for the reader.
const PHASE_TITLE = "title";
const PHASE_BEAT = "beat";
const PHASE_TEASER = "teaser";
const PHASE_BRIDGE = "bridge";
const PHASE_QUIZ = "quiz";

// Estimated reading rate when audio is not yet wired. 150 wpm is roughly
// the founder's natural delivery; we add a 1.5s tail so the next beat does
// not jump the moment the last word finishes.
const FALLBACK_WPM = 150;
const FALLBACK_TAIL_MS = 1500;

function estimateDurationMs(text) {
  const words = text.trim().split(/\s+/).length;
  return Math.max(3000, Math.round((words / FALLBACK_WPM) * 60_000) + FALLBACK_TAIL_MS);
}

function isPlaceholderAudio(src) {
  return !src || src.startsWith("TODO_AUDIO_");
}

// ─── Receipt list (offers, not gates) ──────────────────────────────────
function Receipt({ label, url }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="first-walker-receipt"
      style={{
        display: "block",
        textDecoration: "none",
        borderLeft: `2px solid ${COLORS.ember}`,
        background: "rgba(194,112,61,0.06)",
        padding: "9px 13px",
        marginTop: 6,
        color: COLORS.boneDim,
        fontSize: 12,
        lineHeight: 1.45,
        letterSpacing: "0.01em",
        transition: "background 200ms ease, color 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(194,112,61,0.16)";
        e.currentTarget.style.color = COLORS.bone;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(194,112,61,0.06)";
        e.currentTarget.style.color = COLORS.boneDim;
      }}
    >
      <span style={{ color: COLORS.ember, fontWeight: 600, marginRight: 6, fontSize: 10, letterSpacing: "0.08em" }}>
        RECEIPT
      </span>
      {label}
      <span style={{ float: "right", color: COLORS.emberSoft }}>↗</span>
    </a>
  );
}

// ─── Quiz panel — shown only after the chain plays end-to-end ──────────
function Quiz({ quiz, onComplete }) {
  const [answers, setAnswers] = useState({});
  const allAnswered = quiz.every((_, i) => answers[i] !== undefined);

  return (
    <section
      style={{ maxWidth: 760, margin: "0 auto", padding: "56px 24px" }}
      data-testid="first-walker-quiz"
    >
      <h2
        style={{
          fontFamily: "'Spectral', Georgia, serif",
          fontSize: 28,
          color: COLORS.bone,
          marginBottom: 28,
          fontWeight: 600,
        }}
      >
        Three questions. Just to check you were in the room.
      </h2>
      {quiz.map((item, qi) => {
        const chosen = answers[qi];
        const answered = chosen !== undefined;
        return (
          <div key={`q-${qi}`} style={{ marginBottom: 36 }}>
            <p
              style={{
                fontFamily: "'Newsreader', Georgia, serif",
                fontSize: 18,
                lineHeight: 1.55,
                color: COLORS.bone,
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              {qi + 1}. {item.q}
            </p>
            {item.options.map((opt, oi) => {
              const isChosen = chosen === oi;
              const isCorrect = item.correct === oi;
              let border = COLORS.line;
              let bg = "transparent";
              if (answered && isCorrect) {
                border = COLORS.ember;
                bg = "rgba(194,112,61,0.12)";
              } else if (answered && isChosen && !isCorrect) {
                border = COLORS.emberSoft;
                bg = "rgba(138,88,54,0.10)";
              }
              return (
                <button
                  key={`q-${qi}-opt-${oi}`}
                  disabled={answered}
                  onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                  data-testid={`first-walker-quiz-q${qi + 1}-opt${oi + 1}`}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    background: bg,
                    border: `1px solid ${border}`,
                    color: COLORS.bone,
                    padding: "12px 16px",
                    marginBottom: 8,
                    borderRadius: 6,
                    cursor: answered ? "default" : "pointer",
                    fontSize: 15,
                    fontFamily: "'Newsreader', Georgia, serif",
                    transition: "border 200ms ease, background 200ms ease",
                  }}
                >
                  {opt}
                  {answered && isCorrect && (
                    <span style={{ color: COLORS.ember, float: "right" }}>✓</span>
                  )}
                </button>
              );
            })}
            {answered && (
              <p
                style={{
                  marginTop: 10,
                  fontSize: 14,
                  color: COLORS.boneDim,
                  lineHeight: 1.5,
                  fontStyle: "italic",
                }}
                data-testid={`first-walker-quiz-q${qi + 1}-feedback`}
              >
                {item.feedback}
              </p>
            )}
          </div>
        );
      })}

      {allAnswered && (
        <button
          onClick={onComplete}
          data-testid="first-walker-next-chain-btn"
          style={{
            marginTop: 24,
            background: COLORS.ember,
            color: COLORS.ash,
            border: "none",
            padding: "14px 26px",
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "0.04em",
            cursor: "pointer",
          }}
        >
          {CHAIN.nextLabel} →
        </button>
      )}
    </section>
  );
}

// ─── Main player ───────────────────────────────────────────────────────
export default function TheFirstWalker() {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const fallbackTimerRef = useRef(null);

  // Phase state machine: title → beat (×15) → teaser → bridge → quiz
  const [phase, setPhase] = useState(PHASE_TITLE);
  const [beatIndex, setBeatIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  // Inject Newsreader + Spectral fonts once
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,600;1,6..72,400&family=Spectral:wght@600;700&display=swap";
    document.head.appendChild(link);
    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
    };
  }, []);

  // Compute the current playable item (title / beat / teaser / bridge)
  const current = useMemo(() => {
    if (phase === PHASE_TITLE) {
      return {
        kind: "title",
        image: CHAIN.beats[0].image,
        text: CHAIN.methodologyCard,
        audio: null,
        receipts: [],
      };
    }
    if (phase === PHASE_BEAT) {
      const b = CHAIN.beats[beatIndex];
      return {
        kind: "beat",
        image: b.image,
        text: b.text,
        audio: b.audio,
        receipts: b.receipts,
        weirdFact: b.weirdFact,
        physical: b.physical,
        movement: b.movement,
        beatNumber: beatIndex + 1,
      };
    }
    if (phase === PHASE_TEASER) {
      return {
        kind: "teaser",
        image: CHAIN.teaserImage,
        text: CHAIN.teaser,
        audio: null,
        receipts: CHAIN.teaserReceipts,
        title: "Coming back to Laetoli",
      };
    }
    if (phase === PHASE_BRIDGE) {
      return {
        kind: "bridge",
        image: CHAIN.bridgeImage,
        text: CHAIN.bridge,
        audio: CHAIN.bridgeAudio,
        receipts: [],
      };
    }
    return null;
  }, [phase, beatIndex]);

  // Clear any pending fallback timer
  const clearFallback = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  // Advance state machine
  const advance = useCallback(() => {
    clearFallback();
    setPhase((p) => {
      if (p === PHASE_TITLE) return PHASE_BEAT;
      if (p === PHASE_BEAT) {
        setBeatIndex((bi) => {
          if (bi + 1 < CHAIN.beats.length) return bi + 1;
          // No more beats — transition phase will be handled in next render
          return bi;
        });
        // If we are about to overflow, transition the phase
        // (read latest beatIndex via the setter pattern above; we approximate
        // by checking against the current value at this tick)
        return p;
      }
      if (p === PHASE_TEASER) return PHASE_BRIDGE;
      if (p === PHASE_BRIDGE) return PHASE_QUIZ;
      return p;
    });
  }, [clearFallback]);

  // Beat overflow → teaser. Captured separately because of the closure trap
  // around setPhase + setBeatIndex chained inside `advance`.
  useEffect(() => {
    if (phase === PHASE_BEAT && beatIndex >= CHAIN.beats.length - 1) {
      // We are sitting on the last beat. When it advances, jump to teaser.
      // (This effect does not auto-advance; it sets up the listener.)
    }
  }, [phase, beatIndex]);

  // Custom advance for beat phase that handles overflow → teaser
  const advanceFromBeat = useCallback(() => {
    clearFallback();
    setBeatIndex((bi) => {
      if (bi + 1 < CHAIN.beats.length) return bi + 1;
      setPhase(PHASE_TEASER);
      return bi;
    });
  }, [clearFallback]);

  // Play the current item (audio + fallback)
  const playCurrent = useCallback(() => {
    if (!current) return;
    clearFallback();
    const audio = audioRef.current;
    if (!audio) return;

    // Always reset audio first
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {/* ignore */}

    const advanceFn = phase === PHASE_BEAT ? advanceFromBeat : advance;

    if (isPlaceholderAudio(current.audio)) {
      // No real audio — use word-count timer
      const ms = estimateDurationMs(current.text);
      fallbackTimerRef.current = setTimeout(advanceFn, ms);
      return;
    }

    audio.src = current.audio;
    audio.muted = muted;
    audio.onended = () => advanceFn();
    audio.onerror = () => {
      // Audio URL 404 / decoding failure → fall back to estimated duration
      const ms = estimateDurationMs(current.text);
      fallbackTimerRef.current = setTimeout(advanceFn, ms);
    };
    audio.play().catch(() => {
      // Autoplay blocked (rare after user click). Fall back to timer.
      const ms = estimateDurationMs(current.text);
      fallbackTimerRef.current = setTimeout(advanceFn, ms);
    });
  }, [current, phase, advance, advanceFromBeat, muted, clearFallback]);

  // Drive the player: any time phase/beatIndex changes and `playing` is on,
  // start the current item.
  useEffect(() => {
    if (!playing) return;
    if (phase === PHASE_QUIZ) return; // Quiz waits for reader; no audio
    playCurrent();
    // Pause audio + clear timer on unmount / dep change
    return () => {
      clearFallback();
      const audio = audioRef.current;
      if (audio) {
        try { audio.pause(); } catch {/* ignore */}
        audio.onended = null;
        audio.onerror = null;
      }
    };
  }, [playing, phase, beatIndex, playCurrent, clearFallback]);

  // Pause toggle — pauses both audio and fallback timer
  const handlePauseResume = useCallback(() => {
    setPlaying((p) => {
      if (p) {
        clearFallback();
        const audio = audioRef.current;
        if (audio) {
          try { audio.pause(); } catch {/* ignore */}
        }
        return false;
      }
      return true;
    });
  }, [clearFallback]);

  const handleStart = useCallback(() => {
    setHasStarted(true);
    setPlaying(true);
  }, []);

  const handleBack = useCallback(() => {
    clearFallback();
    if (phase === PHASE_BEAT && beatIndex > 0) {
      setBeatIndex((bi) => bi - 1);
    } else if (phase === PHASE_BEAT && beatIndex === 0) {
      setPhase(PHASE_TITLE);
    } else if (phase === PHASE_TEASER) {
      setPhase(PHASE_BEAT);
      setBeatIndex(CHAIN.beats.length - 1);
    } else if (phase === PHASE_BRIDGE) {
      setPhase(PHASE_TEASER);
    } else if (phase === PHASE_QUIZ) {
      setPhase(PHASE_BRIDGE);
    }
  }, [phase, beatIndex, clearFallback]);

  const handleSkip = useCallback(() => {
    if (phase === PHASE_BEAT) {
      advanceFromBeat();
    } else {
      advance();
    }
  }, [phase, advance, advanceFromBeat]);

  const handleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  }, []);

  const handleQuizComplete = useCallback(() => {
    navigate(CHAIN.nextPath);
  }, [navigate]);

  // ─── Pre-play title card (press play to begin) ────────────────────────
  if (!hasStarted) {
    return (
      <div
        style={{
          background: COLORS.ash,
          minHeight: "100vh",
          color: COLORS.bone,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
        data-testid="first-walker-root"
      >
        <div
          style={{
            maxWidth: 720,
            textAlign: "center",
          }}
        >
          <p
            style={{
              color: COLORS.ember,
              letterSpacing: "0.22em",
              fontSize: 13,
              textTransform: "uppercase",
              marginBottom: 18,
            }}
            data-testid="first-walker-eyebrow"
          >
            {CHAIN.number}
          </p>
          <h1
            style={{
              fontFamily: "'Spectral', Georgia, serif",
              fontSize: 64,
              lineHeight: 1.05,
              margin: 0,
              color: COLORS.bone,
              fontWeight: 700,
            }}
            data-testid="first-walker-title"
          >
            {CHAIN.title}
          </h1>
          <div
            style={{
              marginTop: 36,
              borderTop: `1px solid ${COLORS.line}`,
              borderBottom: `1px solid ${COLORS.line}`,
              padding: "22px 24px",
              color: COLORS.boneDim,
              fontSize: 15,
              lineHeight: 1.6,
              fontFamily: "'Newsreader', Georgia, serif",
              fontStyle: "italic",
              maxWidth: 600,
              margin: "36px auto",
            }}
            data-testid="first-walker-methodology-card"
          >
            {CHAIN.methodologyCard}
          </div>
          <button
            onClick={handleStart}
            data-testid="first-walker-start-btn"
            style={{
              marginTop: 12,
              background: COLORS.ember,
              color: COLORS.ash,
              border: "none",
              padding: "18px 38px",
              borderRadius: 999,
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "0.06em",
              cursor: "pointer",
              textTransform: "uppercase",
            }}
          >
            ▶ Press play to begin
          </button>
          <p
            style={{
              marginTop: 18,
              fontSize: 12,
              color: COLORS.boneDim,
              letterSpacing: "0.04em",
            }}
          >
            Voice-led. Hands-free. Sit back. About 12 minutes.
          </p>
        </div>
      </div>
    );
  }

  // ─── Quiz phase — independent layout, no player chrome ────────────────
  if (phase === PHASE_QUIZ) {
    return (
      <div
        style={{
          background: COLORS.ash,
          minHeight: "100vh",
          color: COLORS.bone,
          paddingBottom: 80,
        }}
        data-testid="first-walker-root"
        data-phase={PHASE_QUIZ}
      >
        <header
          style={{
            maxWidth: 760,
            margin: "0 auto",
            padding: "64px 24px 8px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              color: COLORS.ember,
              letterSpacing: "0.22em",
              fontSize: 12,
              textTransform: "uppercase",
              marginBottom: 14,
            }}
          >
            {CHAIN.number}
          </p>
          <h1
            style={{
              fontFamily: "'Spectral', Georgia, serif",
              fontSize: 44,
              lineHeight: 1.05,
              margin: 0,
              color: COLORS.bone,
              fontWeight: 700,
            }}
          >
            {CHAIN.title}
          </h1>
        </header>
        <Quiz quiz={CHAIN.quiz} onComplete={handleQuizComplete} />
      </div>
    );
  }

  // ─── Playback layout ──────────────────────────────────────────────────
  const totalBeats = CHAIN.beats.length;
  const movementLabel =
    phase === PHASE_TITLE
      ? "Opening"
      : phase === PHASE_BEAT
      ? MOVEMENTS.find((m) => m.key === current.movement)?.label || ""
      : phase === PHASE_TEASER
      ? "Coming back to Laetoli"
      : "The bridge";

  return (
    <div
      style={{
        background: COLORS.ash,
        minHeight: "100vh",
        color: COLORS.bone,
        position: "relative",
        overflow: "hidden",
      }}
      data-testid="first-walker-root"
      data-phase={phase}
      data-beat-index={phase === PHASE_BEAT ? beatIndex : undefined}
    >
      {/* Top bar — eyebrow + movement + progress dots */}
      <header
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          padding: "20px 28px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 10,
          background: "linear-gradient(to bottom, rgba(13,12,10,0.86), rgba(13,12,10,0))",
        }}
      >
        <div>
          <p
            style={{
              color: COLORS.ember,
              letterSpacing: "0.18em",
              fontSize: 11,
              textTransform: "uppercase",
              margin: 0,
            }}
          >
            {CHAIN.number}
          </p>
          <p
            style={{
              fontFamily: "'Spectral', Georgia, serif",
              color: COLORS.bone,
              fontSize: 16,
              fontWeight: 600,
              margin: "4px 0 0",
            }}
            data-testid="first-walker-header-title"
          >
            {CHAIN.title} · <span style={{ color: COLORS.boneDim, fontWeight: 400 }}>{movementLabel}</span>
          </p>
        </div>

        {/* Beat progress dots */}
        <div
          style={{ display: "flex", gap: 6, alignItems: "center" }}
          data-testid="first-walker-progress"
        >
          {CHAIN.beats.map((_, i) => {
            let bg = COLORS.line;
            if (phase === PHASE_BEAT && i < beatIndex) bg = COLORS.emberSoft;
            else if (phase === PHASE_BEAT && i === beatIndex) bg = COLORS.ember;
            else if (phase === PHASE_TEASER || phase === PHASE_BRIDGE) bg = COLORS.emberSoft;
            return (
              <span
                key={`dot-${i}`}
                style={{
                  width: i === beatIndex && phase === PHASE_BEAT ? 18 : 6,
                  height: 6,
                  borderRadius: 999,
                  background: bg,
                  transition: "width 240ms ease, background 240ms ease",
                }}
              />
            );
          })}
        </div>
      </header>

      {/* 16:9 image stage */}
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "92px 24px 24px",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
            background: `linear-gradient(135deg, ${COLORS.ashSoft}, ${COLORS.ash})`,
            border: `1px solid ${COLORS.line}`,
            overflow: "hidden",
            borderRadius: 6,
          }}
          data-testid="first-walker-stage"
        >
          {current?.image && (
            <img
              key={current.image}
              src={current.image}
              alt=""
              data-testid="first-walker-image"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                animation: "tsogn-fw-fade 800ms ease",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}

          {/* Weird fact / Try this badges */}
          {current?.weirdFact && (
            <span style={badgeStyle()} data-testid="first-walker-weird-badge">
              WEIRD FACT
            </span>
          )}
          {current?.physical && (
            <span style={badgeStyle()} data-testid="first-walker-physical-badge">
              TRY THIS
            </span>
          )}

          {/* Receipts panel — bottom-right, offer not gate */}
          {current?.receipts && current.receipts.length > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: 14,
                right: 14,
                maxWidth: 360,
                width: "min(360px, 60%)",
                pointerEvents: "auto",
              }}
              data-testid="first-walker-receipts"
            >
              {current.receipts.map((r, i) => (
                <Receipt key={`r-${i}`} label={r.label} url={r.url} />
              ))}
            </div>
          )}
        </div>

        {/* Narration text — quietly tracks the voice */}
        <div
          style={{
            marginTop: 28,
            maxWidth: 780,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <p
            style={{
              fontFamily: "'Newsreader', Georgia, serif",
              fontSize: 21,
              lineHeight: 1.7,
              color: COLORS.bone,
              margin: 0,
              fontWeight: phase === PHASE_TITLE ? 400 : 400,
              fontStyle: phase === PHASE_TITLE ? "italic" : "normal",
              textAlign: phase === PHASE_TITLE ? "center" : "left",
            }}
            data-testid="first-walker-narration"
          >
            {current?.text}
          </p>
        </div>
      </div>

      {/* Movement spine — quiet reference for where you are in the chain */}
      <nav
        style={{
          maxWidth: 780,
          margin: "32px auto 12px",
          padding: "0 24px",
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
        data-testid="first-walker-spine"
      >
        {MOVEMENTS.map((m) => {
          const isActive = phase === PHASE_BEAT && current?.movement === m.key;
          return (
            <span
              key={m.key}
              style={{
                fontSize: 10,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: isActive ? COLORS.ember : COLORS.boneDim,
                border: `1px solid ${isActive ? COLORS.ember : COLORS.line}`,
                borderRadius: 999,
                padding: "5px 12px",
                transition: "color 240ms ease, border 240ms ease",
              }}
            >
              {m.label}
            </span>
          );
        })}
      </nav>

      {/* Bottom controls — minimal, fade-in pill */}
      <div
        style={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(13,12,10,0.85)",
          border: `1px solid ${COLORS.line}`,
          borderRadius: 999,
          padding: "10px 18px",
          display: "flex",
          gap: 14,
          alignItems: "center",
          zIndex: 20,
          backdropFilter: "blur(8px)",
        }}
        data-testid="first-walker-controls"
      >
        <button
          onClick={handleBack}
          data-testid="first-walker-back-btn"
          aria-label="Back one beat"
          style={controlBtnStyle()}
        >
          ◄◄
        </button>
        <button
          onClick={handlePauseResume}
          data-testid="first-walker-pause-btn"
          aria-label={playing ? "Pause" : "Resume"}
          style={{
            ...controlBtnStyle(),
            color: COLORS.ember,
            fontSize: 16,
          }}
        >
          {playing ? "▮▮" : "▶"}
        </button>
        <button
          onClick={handleSkip}
          data-testid="first-walker-skip-btn"
          aria-label="Skip one beat"
          style={controlBtnStyle()}
        >
          ►►
        </button>
        <span style={{ width: 1, height: 18, background: COLORS.line }} />
        <button
          onClick={handleMute}
          data-testid="first-walker-mute-btn"
          aria-label={muted ? "Unmute" : "Mute"}
          style={controlBtnStyle()}
        >
          {muted ? "🔇" : "🔊"}
        </button>
        <span
          style={{
            fontSize: 11,
            color: COLORS.boneDim,
            letterSpacing: "0.06em",
            marginLeft: 4,
          }}
          data-testid="first-walker-counter"
        >
          {phase === PHASE_BEAT && `${beatIndex + 1} / ${totalBeats}`}
          {phase === PHASE_TITLE && `Opening`}
          {phase === PHASE_TEASER && `Coming next`}
          {phase === PHASE_BRIDGE && `Bridge`}
        </span>
      </div>

      {/* Hidden audio element — drives the playback engine */}
      <audio ref={audioRef} preload="auto" />

      {/* Keyframe animation for image fade */}
      <style>{`
        @keyframes tsogn-fw-fade {
          from { opacity: 0; transform: scale(1.02); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

function badgeStyle() {
  return {
    position: "absolute",
    top: 14,
    left: 14,
    background: COLORS.ember,
    color: COLORS.ash,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.1em",
    padding: "4px 10px",
    borderRadius: 4,
  };
}

function controlBtnStyle() {
  return {
    background: "transparent",
    border: "none",
    color: COLORS.bone,
    fontSize: 13,
    cursor: "pointer",
    padding: "6px 10px",
    letterSpacing: "0.06em",
  };
}
